// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { Component, ElementRef, ViewChild, signal, Renderer2, HostListener } from '@angular/core';
import { SidebarService } from '../../../services/sidebar.service';
import { ApiService } from '../../../services/api.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-camera',
  standalone: false,
  templateUrl: './camera.component.html',
  styleUrls: ['./camera.component.css'],
})
export class CameraComponent {
  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;

  private stream!: MediaStream;
  private currentCamera: 'user' | 'environment' = 'environment';
  capturedImage = signal<string | null>(null);
  lightboxOpen = false;

  constructor(private sidebarService: SidebarService,
    private renderer: Renderer2,
    private toastService: ToastService,
    private apiService: ApiService) { }

  async ngAfterViewInit() {
    // this.sidebarService.hide();
    await this.startCamera();
  }

  async startCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }

    this.stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: this.currentCamera },
    });

    if (this.videoElement.nativeElement) {
      this.videoElement.nativeElement.srcObject = this.stream;

      // Apply mirroring only for the front camera
      if (this.currentCamera === 'user') {
        this.renderer.addClass(this.videoElement.nativeElement, 'video-mirror');
      } else {
        this.renderer.removeClass(this.videoElement.nativeElement, 'video-mirror');
      }
    }
  }

  switchCamera() {
    this.currentCamera = this.currentCamera === 'user' ? 'environment' : 'user';
    this.startCamera();
  }

  captureImage() {
    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas size to video feed size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Check if mirroring is applied
    if (this.currentCamera === 'user') {
      // Flip horizontally before drawing
      context.scale(-1, 1);
      context.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
    } else {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    this.capturedImage.set(canvas.toDataURL('image/png'));
  }

  dataURLToFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  uploadImage() {
    if (!this.capturedImage()) {
      console.error("No image to upload");
      return;
    }

    const formData = new FormData();
    const file = this.dataURLToFile(this.capturedImage()!, "captured-image.png");
    formData.append('file', file);

    this.apiService.postData(`gallery/uploadPublicImage`, formData).subscribe({
      next: (response: any) => {
        this.toastService.success("Image uploaded");
        this.capturedImage.set(null);
        this.closeLightbox();
      },
      error: (error: any) => {
        console.error("Upload error:", error);
        this.toastService.error("Failed to upload image");
      }
    });
  }

  ngOnDestroy() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
    this.sidebarService.show();
  }

  deleteImage() {
    this.capturedImage.set(null);
    this.closeLightbox();
  }

  closeLightbox() {
    this.lightboxOpen = false;
  }

  openLightbox() {
    this.lightboxOpen = true;
  }


  @HostListener('window:popstate', ['$event'])
  onPopState(event: Event) {
    if (this.lightboxOpen) {
      this.closeLightbox();
      event.stopPropagation();
      event.preventDefault();
      event.cancelBubble = true;
    }
  }
}
